import { useEffect, useState } from 'react'
import MFAccordion from '../../components/ui-components/MFAccordion'
import { EventNames } from '../../types/misc.types'
import { disableHardwareBack, lightImpactVibrate, postCTA, trackEvent, updateAppStatusBar, onReady } from '../../utils/appBridge'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchRegularToDirectFundDetail } from '../../repository/regular-to-direct-plan'
import { useNavigate, useParams } from 'react-router-dom'
import { MutualFundListItem } from '../../components/power-rebalancing/MutualFundList'
import BackButton from '../../components/ui-components/BackButton'
import ExitLoadAndTax from '../../components/power-rebalancing/tax/ExitLoadAndTax'
import { getMutualFundDetailCTA } from '../../utils/mutualFundDetail'
import PeerAnalyserError from '../../components/error-states/PeerAnalyserError'
import { PowerPerformanceForms } from '../../types/mutual-fund-detail.types'
import { getTopPadding } from '../../utils/sessionStorage'
import { RegularToDirectFundDetailResponse } from '../../types/regular-to-direct-plan.types'
import RebalanceHeaderForRegularToDirectPlan from '../../components/regular-to-direct-plan/RebalanceHeaderForRegularToDirectPlan'
import TransferRiveComponent from '../../components/Questionnaire/TransferRiveComponent'
import ReasonsToRebalance from '../../components/power-rebalancing/ReasonsToRebalance'
import RebalancingDisclaimer from '../../components/power-rebalancing/RebalancingDisclaimer'
import { formatIndianNumberWithDecimals } from '../../utils/FD/fd'
import Info from '../../components/ui-components/Info'
import { RegularToDirectPlanFAQs } from '../../components/regular-to-direct-plan/RegularToDirectPlanFAQs'
import { One } from '../../icons/rebalance/One'
import { roundOffAndFormatNumberWithCurrency } from '../../utils/utils'
import { Two } from '../../icons/rebalance/Two'
import { Three } from '../../icons/rebalance/Three'

const toPerformanceForm = (rating: any): PowerPerformanceForms => {
  if (Object.values(PowerPerformanceForms).includes(rating as PowerPerformanceForms)) {
    return rating as PowerPerformanceForms
  }
  return PowerPerformanceForms.NOT_RATED
}

const RegularToDirectFundComparison = (fundDetail: RegularToDirectFundDetailResponse) => {
  const { isin, name, purchaseMode, powerRank, performanceRating, mfLogoUrl, directFundData } = fundDetail

  const performanceForm = toPerformanceForm(performanceRating)
  const directPerformanceForm = toPerformanceForm(directFundData.performanceRating)

  return (
    <div className='relative'>
      <div className='mx-5 rounded-xl bg-grey80 pb-[2px] z-10 relative'>
        <div className='text-grey50 text-13 py-1 px-3 font-medium'>Current</div>
        <div className='bg-white mx-[2px] rounded-xl'>
          <MutualFundListItem
            isin={isin}
            name={name}
            fundNature={directFundData.fundNature}
            fundCategory={directFundData.fundCategory}
            purchaseMode={purchaseMode}
            powerRank={powerRank}
            performanceRating={performanceForm}
            missedGains={0}
            highExposure={false}
            mfLogoUrl={mfLogoUrl}
            sipAmount={0}
            isRegular={purchaseMode === 'REGULAR'}
            formType={performanceForm}
            investedAmount={fundDetail.investedAmount}
            onClick={() => {
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  eventId: 'RegularFundClick',
                  responseName: 'Click',
                  fundIsin: isin,
                  screenType: `REGULAR_TO_DIRECT_PLAN_L2_PAGE`
                }
              })
              postCTA(JSON.stringify(getMutualFundDetailCTA({ isin, navigationStyle: 'PUSH' })))
            }}
            borderRequired={false}
            riskProfile={null}
          />
        </div>
      </div>
      <div className='max-w-fit mx-5 h-full relative z-1 max-h-[72px]'>
        <div className='z-10 absolute text-center w-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-status-text-in-form text-13 font-medium'>
          Transfer {fundDetail?.taxDetails?.currentValue > 0 ? `₹${formatIndianNumberWithDecimals(fundDetail?.taxDetails?.currentValue, 0)}` : ''} Lumpsum
        </div>
        <TransferRiveComponent riveUrl='/rives/risk-profiles/transfer.riv' />
      </div>
      <div className='mx-5 rounded-xl bg-status-in-form pb-[2px] z-10 relative'>
        <div className='text-13 py-1 px-3 text-status-text-in-form font-medium'>Direct Plan</div>
        <div className='bg-white mx-[2px] rounded-xl'>
          <MutualFundListItem
            isin={directFundData.isin}
            name={directFundData.name}
            fundNature={directFundData.fundNature}
            fundCategory={directFundData.fundCategory}
            purchaseMode='DIRECT'
            powerRank={directFundData.powerRank}
            performanceRating={directPerformanceForm}
            missedGains={0}
            highExposure={false}
            mfLogoUrl={directFundData.mfLogoUrl}
            sipAmount={0}
            isRegular={false}
            formType={directPerformanceForm}
            investedAmount={fundDetail.investedAmount}
            onClick={() => {
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  eventId: 'DirectFundClick',
                  responseName: 'Click',
                  fundIsin: directFundData.isin,
                  screenType: `REGULAR_TO_DIRECT_PLAN_L2_PAGE`
                }
              })
              postCTA(JSON.stringify(getMutualFundDetailCTA({ isin: directFundData.isin, navigationStyle: 'PUSH' })))
            }}
            borderRequired={false}
            riskProfile={null}
          />
        </div>
      </div>
      {
        fundDetail.currentWealth - fundDetail?.taxDetails?.currentValue >= 1 &&
        <div className='mx-5 mt-4'>
          <div className='text-13 text-grey40 border-[#CBF7D7] border-[1px] rounded-xl px-4 py-2 inline-flex gap-3 w-full relative'>
            <div className='self-center'>
              <svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' fill='none'>
                <g fillRule='evenodd' clipPath='url(#a)' clipRule='evenodd'>
                  <path fill='#CBF7D7' d='M20 28c5.302 0 8-2.698 8-8s-2.698-8-8-8-8 2.698-8 8 2.698 8 8 8Z' />
                  <path
                    fill='#00661D'
                    d='M18.377 22.207a.668.668 0 0 0-.677.66c0 .364.303.66.677.66h3.247a.668.668 0 0 0 .676-.66.668.668 0 0 0-.676-.66h-.947v-3.035a.668.668 0 0 0-.677-.66h-1.082a.668.668 0 0 0-.677.66c0 .364.303.66.677.66h.406v2.375h-.947Zm2.706-5.675A1.07 1.07 0 0 1 20 17.588a1.07 1.07 0 0 1-1.082-1.056A1.07 1.07 0 0 1 20 15.477a1.07 1.07 0 0 1 1.082 1.055Z'
                  />
                </g>
                <defs>
                  <clipPath id='a'>
                    <path fill='#fff' d='M12 12h16v16H12z' />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className='text-grey10 self-center mb-[2px]'>
            {fundDetail.elss ? `The remaining ₹${formatIndianNumberWithDecimals(fundDetail.currentWealth - fundDetail?.taxDetails?.currentValue || 0, 0)} is under review since it’s under lock-in. We’ll let you know when to switch.` : 
              `The remaining ₹${formatIndianNumberWithDecimals(fundDetail.currentWealth - fundDetail?.taxDetails?.currentValue || 0, 0)} is under review as switching now will attract high STCG tax. We'll let you know when
              to switch.`}
            </div>
          </div>
        </div>
      }
    </div>
  )
}

export default function RegularToDirectPlanL2Page() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { isin } = useParams()
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['regular-to-direct-fund-detail', isin],
    queryFn: () => fetchRegularToDirectFundDetail(isin!),
    retry: 1,
    enabled: !!isin
  })

  useEffect(() => {
    disableHardwareBack()
    updateAppStatusBar('FFFFFF', 'dark')
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        screenType: `REGULAR_TO_DIRECT_PLAN_L2_PAGE`
      }
    })
  }, [isin])

  useEffect(() => {
    if (!isLoading && !isError && data) {
      onReady()
    }
  }, [isLoading, isError, data])

  const handleBack = () => {
    lightImpactVibrate()
    navigate(-1)
  }

  return (
    <div className={`h-full w-full overflow-hidden ${isLoading || isError ? 'bg-white' : 'bg-grey80'}`}>
      <div
        style={{ height: `calc(${getTopPadding()}px + 54px)` }}
        key='small'
        id='fullHeaderSmall'
        className='w-full flex z-[99] bg-white fixed top-0 left-0 right-0'
      >
        <div
          style={{ paddingTop: `calc(${getTopPadding()}px + 16px)` }}
          className={`flex items-center relative w-full pb-5 pr-5 pl-[19px] h-full bg-white`}
        >
          <div className='flex absolute z-1'>
            <BackButton size={24} onClick={handleBack} />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className='p-5'>
          <div className='flex flex-col gap-4'>
            <div className='h-[9px] max-w-[72px] bg-grey80 animate-pulse rounded'></div>
            <div className='w-[200px] h-[15px] bg-grey80 animate-pulse rounded'></div>
          </div>
        </div>
      ) : isError ? (
        <div className='flex items-center h-[calc(100vh-80px)] relative mb-[20px] mx-5'>
          <PeerAnalyserError
            onRetry={() => {
              queryClient.invalidateQueries({ queryKey: ['regular-to-direct-fund-detail', isin] })
            }}
          />
        </div>
      ) : (
        <div
          style={{ marginTop: `calc(${getTopPadding()}px + 54px)`, height: `calc(100vh - ${getTopPadding()}px - 54px)` }}
          className={`w-full flex flex-col flex-1 overflow-y-scroll no-scrollbar overflow-x-hidden overscroll-none bg-grey80`}
        >
          {data?.exited && <div className='bg-white px-5 pb-6'>
            <Info
              className='!gap-3 px-4 py-3 bg-status-on-track rounded-xl'
              lineClassName='!w-[3px] bg-status-text-in-form'
              contentClassName='text-13 text-status-text-in-form'
            >
              You've already withdrawn the suggested amount from this fund.<br />
            </Info>
          </div>}
          <div className='bg-white pb-8'>
            <div className='px-5 flex flex-col gap-3'>
              <div className='text-orange30 text-13 font-medium'>Regular to Direct recommendation</div>
              {data && <RebalanceHeaderForRegularToDirectPlan headingValue={data?.taxDetails?.currentValue || 0} />}
            </div>
            {data && <RegularToDirectFundComparison {...data} />}
            {data?.taxDetails?.currentValue && 
              <div className='px-5 border rounded-xl mt-8 border-[#EBF2F2] mx-5'>
                <MFAccordion
                  title='Steps to switch'
                  icon={'/images/steps_to_rebalance_tick.png'}
                  middleContentTitleClassName='!text-base !font-normal'
                  accordionHeaderClassName='!p-0'
                  iconClassName='!w-[22px] !h-[22px]'
                  isExpanded={isAccordionOpen}
                  isBorderTop={false}
                  chevronTopPadding='40%'
                  accordionBodyClassName='!p-0'
                  onAccordionClick={() => {
                    setIsAccordionOpen((prev) => {
                      trackEvent({
                        eventName: EventNames.UserClicked,
                        payload: {
                          eventId: 'StepsToSwitch',
                          responseName: 'Expand',
                          screenType: `REGULAR_TO_DIRECT_PLAN_L2_PAGE`
                        }
                      })
                      return !prev
                    })
                  }}
                >
                  <div className='flex flex-col gap-4 mb-4'>
                    <h2 className='text-13 font-medium text-blue30'>Lumpsum Rebalance</h2>
                    <ol className='flex flex-col gap-6'>
                      <li className='flex items-center gap-3'>
                        <span className='self-start'>
                          <One />
                        </span>
                        Redeem {roundOffAndFormatNumberWithCurrency(data?.taxDetails.currentValue || 0)}{' '}
                        from {data?.name}
                      </li>
                      <li className='flex items-center gap-3'>
                        <span className='self-start'>
                          <Two />
                        </span>
                        Wait for the amount to be credited to your bank (usually in 3-4 working days)
                      </li>
                      <li className='flex items-center gap-3'>
                        <span className='self-start'>
                          <Three />
                        </span>
                        Re-invest the same amount in {data?.directFundData?.name} as lump-sum
                      </li>
                    </ol>
                  </div>
                  <div className='flex flex-col gap-4 mb-4'>
                    <h2 className='text-13 font-medium text-blue30'>SIP Rebalance</h2>
                    <ol className='flex flex-col gap-6'>
                      <li className='flex items-center gap-3'>
                        <span className='self-start'>
                          <One />
                        </span>
                        Cancel existing SIP in {data?.name}
                      </li>
                      <li className='flex items-center gap-3'>
                        <span className='self-start'>
                          <Two />
                        </span>
                        Start a new SIP in {data?.directFundData.name} with the same amount
                      </li>
                    </ol>
                  </div>
                  <div className='text-13 text-grey40 mb-4'>
                    Note: Make sure the new fund mentions 'Direct' either in its name or in the fund's details.
                  </div>
                </MFAccordion>
              </div>
            }
          </div>
          {data && (
            <>
              <div className='bg-white mt-3'>
                <ReasonsToRebalance
                  currentWealth={data.currentWealth}
                  missedGainsAsOfDate={Date.now()}
                  isRegularFund={data.purchaseMode === 'REGULAR'}
                  additionalCommissionsPaid={data.additionalCommissionsPaid}
                  isin={data.isin}
                  directFundISIN={data.directFundData?.isin}
                />
              </div>
              {data.taxDetails && (
                <div className='mt-3'>
                  <ExitLoadAndTax
                    isElss={data.directFundData.fundCategory === 'ELSS_TAX_SAVINGS'}
                    isRegularFund={true}
                    isHighExposureFund={false}
                    currentWealth={data.currentWealth}
                    {...data.taxDetails}
                    taxCategory={data.taxDetails.taxCategory as any}
                  />
                </div>
              )}
            </>
          )}
          <RegularToDirectPlanFAQs screenType={`REGULAR_TO_DIRECT_PLAN_L2_PAGE`} />
          <RebalancingDisclaimer />
        </div>
      )}
    </div>
  )
}
